/**
 * The grammar for markdown inline links and images, `[text](destination "title")`,
 * as marked's inline link tokenizer reads it when RichText renders a message.
 * Code that needs to know where a link starts or ends uses this module, so it
 * agrees with what the reader sees: the destination may hold balanced or
 * backslash-escaped parentheses, as Twist stores them, or be wrapped in `<>`.
 *
 * Only marked's link rule is ported, not the precedence of its other tokens:
 * a link inside a code span, a `<scheme:…>` autolink or emphasis delimiters
 * that marked would read first is still found here. Code spans and code
 * blocks are not skipped; callers that must leave code alone handle them.
 *
 * The backend reads push notification text and reply targets with its own
 * copy, `backend/src/core/link-syntax.ts`; a backend test keeps the two the same.
 */

/** A link or image found in markdown, with offsets into the searched string. */
export interface MarkdownLink {
  /** Index of the opening `[`, or of the `!` that starts an image. */
  start: number;
  /** Index just past the link, normally its closing `)`. */
  end: number;
  image: boolean;
  /** The link text as written between the brackets. */
  text: string;
  /** Index where `text` starts. */
  textStart: number;
  /** The destination as written, including any `<>` and backslash escapes. */
  destination: string;
  /** The destination marked renders as the href: `<>` removed, escapes resolved. */
  href: string;
  /** The title with its quotes removed and escapes resolved, or "". */
  title: string;
}

const ESCAPED_PUNCTUATION = /\\([\p{P}\p{S}])/gu;

/** Whether `code` is a character JavaScript's `\s` matches. */
function isSpace(code: number): boolean {
  return code === 32 || (code >= 9 && code <= 13) || code === 0xa0 || code === 0x1680
    || (code >= 0x2000 && code <= 0x200a) || code === 0x2028 || code === 0x2029
    || code === 0x202f || code === 0x205f || code === 0x3000 || code === 0xfeff;
}

const LINE_TERMINATORS = "\n\r\u2028\u2029";

/** Where a link's destination, title and closing `)` lie, after its `](`. */
interface LinkTail {
  destStart: number;
  destEnd: number;
  titleStart: number;
  titleEnd: number;
  end: number;
}

/**
 * The result of marked 17's `inline.link` regex,
 * `!?\[(label)\]\(\s*(href)(?:[ \t]*(?:\n[ \t]*)?(title))?\s*\)`, found
 * without the regex. As a regex it backtracks in cubic time on some inputs,
 * such as a long run of `[a](`, and this module also runs on text marked
 * never tokenizes, such as code blocks and messages shown only as previews.
 * Each position's result here is worked out once, from tables built in one
 * pass over the string, following the order the regex tries its alternatives
 * in. Each function below that finds links builds one scanner for each
 * string it reads.
 */
class LinkScanner {
  private readonly n: number;
  /** The first index at or after i that `\s` doesn't match. */
  private readonly nextNonSpace: Int32Array;
  /** The last index at or before i that `\s` doesn't match, or -1. */
  private readonly prevNonSpace: Int32Array;
  /** The number of backslashes ending at i. */
  private readonly backslashRun: Int32Array;
  /** Parenthesis depth before i, counting only parentheses no backslash
   *  escapes, and the first index after i where it is one lower. */
  private readonly depth: Int32Array;
  private readonly nextDepthDrop: Int32Array;
  /** The first `)` at or after i. */
  private readonly nextCloseParen: Int32Array;
  /** The first index at or after i that isn't a space or tab. */
  private readonly nextNonBlank: Int32Array;
  /** The first index at or after i that can't be in a bare destination. */
  private readonly bareEnd: Int32Array;
  /** Per closing character of a title: the first one at or after i that no
   *  backslash precedes, which a title can't run past. */
  private readonly nextHardClose = new Map<string, Int32Array>();
  /** Per closing character of a title: the last one at or before i that a
   *  backslash precedes and `\s*\)` follows. */
  private readonly prevSoftClose = new Map<string, Int32Array>();
  /** For a bare destination ending before i: -1 if nothing can follow it,
   *  -2 if `\s*\)` can, or the index of the title's closing character; and
   *  the last index at or before i where one can end. */
  private readonly endsBareDestination: Int32Array;
  private readonly prevEndsBareDestination: Int32Array;
  /** The index of the `]` that ends a link's text starting at i, or -1. */
  private readonly textEnd: Int32Array;
  private readonly tails = new Map<number, LinkTail>();
  /** The first `[` at or after i that no backslash escapes and that opens a
   *  link, or n. */
  private readonly nextOpen: Int32Array;

  constructor(private readonly src: string) {
    const n = (this.n = src.length);
    const code = (i: number) => src.charCodeAt(i);

    this.nextNonSpace = new Int32Array(n + 1).fill(n);
    this.nextNonBlank = new Int32Array(n + 1).fill(n);
    this.bareEnd = new Int32Array(n + 1).fill(n);
    for (let i = n - 1; i >= 0; i--) {
      this.nextNonSpace[i] = isSpace(code(i)) ? this.nextNonSpace[i + 1]! : i;
      this.nextNonBlank[i] = src[i] === " " || src[i] === "\t" ? this.nextNonBlank[i + 1]! : i;
      this.bareEnd[i] = code(i) <= 32 ? i : this.bareEnd[i + 1]!;
    }

    this.prevNonSpace = new Int32Array(n).fill(-1);
    this.backslashRun = new Int32Array(n).fill(0);
    this.depth = new Int32Array(n + 1);
    this.nextCloseParen = new Int32Array(n + 1).fill(n);
    for (let i = 0; i < n; i++) {
      this.prevNonSpace[i] = isSpace(code(i)) ? (i > 0 ? this.prevNonSpace[i - 1]! : -1) : i;
      this.backslashRun[i] = src[i] === "\\" ? (i > 0 ? this.backslashRun[i - 1]! : 0) + 1 : 0;
      // A backslash at an odd place in its run escapes the next character.
      const escaped = i > 0 && this.backslashRun[i - 1]! % 2 === 1;
      this.depth[i + 1] = this.depth[i]! + (escaped ? 0 : src[i] === "(" ? 1 : src[i] === ")" ? -1 : 0);
    }
    this.nextDepthDrop = new Int32Array(n + 1).fill(n + 1);
    const firstAtDepth = new Int32Array(2 * n + 3).fill(n + 1);
    for (let i = n; i >= 0; i--) {
      this.nextDepthDrop[i] = firstAtDepth[this.depth[i]! - 1 + n + 1]!;
      firstAtDepth[this.depth[i]! + n + 1] = i;
      if (i < n) this.nextCloseParen[i] = src[i] === ")" ? i : this.nextCloseParen[i + 1]!;
    }

    for (const close of ['"', "'", ")"]) {
      const hard = new Int32Array(n + 1).fill(n);
      for (let i = n - 1; i >= 0; i--) hard[i] = src[i] === close && src[i - 1] !== "\\" ? i : hard[i + 1]!;
      const soft = new Int32Array(n).fill(-1);
      for (let i = 1; i < n; i++) {
        soft[i] = src[i] === close && src[i - 1] === "\\" && this.closesLink(i) ? i : soft[i - 1]!;
      }
      this.nextHardClose.set(close, hard);
      this.prevSoftClose.set(close, soft);
    }

    this.endsBareDestination = new Int32Array(n).fill(-1);
    this.prevEndsBareDestination = new Int32Array(n).fill(-1);
    for (let i = 0; i < n; i++) {
      if (code(i) > 32) {
        const titleClose = this.titleClose(i);
        if (titleClose >= 0) this.endsBareDestination[i] = titleClose;
        else if (src[this.nextNonSpace[i]!] === ")") this.endsBareDestination[i] = -2;
      }
      const ends = this.endsBareDestination[i]! !== -1;
      this.prevEndsBareDestination[i] = ends ? i : i > 0 ? this.prevEndsBareDestination[i - 1]! : -1;
    }

    // A bracketed group in the text, `[…]`, ends at the first `]`, or fails
    // at a `[`; a backslash takes the character after it.
    const groupEnd = new Int32Array(n + 2).fill(-1);
    const runEnd = new Int32Array(n + 1).fill(n);
    const nextBacktick = new Int32Array(n + 1).fill(n);
    for (let i = n - 1; i >= 0; i--) {
      const ch = src[i];
      groupEnd[i] = ch === "]" ? i : ch === "[" ? -1 : ch === "\\" ? (i + 1 < n ? groupEnd[i + 2]! : -1) : groupEnd[i + 1]!;
      runEnd[i] = ch === "`" ? runEnd[i + 1]! : i;
      nextBacktick[i] = ch === "`" ? i : nextBacktick[i + 1]!;
    }
    // The text is a lazy run of groups, escapes, code spans and other
    // characters, ending at the first `](` whose tail matches. Only a
    // backtick run offers a choice: the regex first tries it as a code span
    // reaching the next run, then, for a run of two or more, as the run alone.
    const textEnd = (this.textEnd = new Int32Array(n + 2).fill(-1));
    for (let i = n - 1; i >= 0; i--) {
      const ch = src[i];
      if (ch === "]") {
        textEnd[i] = this.tail(i) ? i : -1;
      } else if (ch === "[") {
        const g = groupEnd[i + 1]!;
        textEnd[i] = g >= 0 ? textEnd[g + 1]! : -1;
      } else if (ch === "\\") {
        textEnd[i] = i + 1 < n ? textEnd[i + 2]! : -1;
      } else if (ch === "`") {
        const run = runEnd[i]!;
        const next = nextBacktick[run]!;
        const asSpan = next < n ? textEnd[runEnd[next]!]! : -1;
        textEnd[i] = asSpan >= 0 ? asSpan : run - i >= 2 ? textEnd[run]! : -1;
      } else {
        textEnd[i] = textEnd[i + 1]!;
      }
    }

    this.nextOpen = new Int32Array(n + 1).fill(n);
    for (let i = n - 1; i >= 0; i--) {
      const opens = src[i] === "[" && !this.isEscaped(i) && this.match(i + 1) !== null;
      this.nextOpen[i] = opens ? i : this.nextOpen[i + 1]!;
    }
  }

  /** Whether a backslash escapes the character at i. */
  private isEscaped(i: number): boolean {
    return i > 0 && this.backslashRun[i - 1]! % 2 === 1;
  }

  /** Whether `\s*\)` follows index i. */
  private closesLink(i: number): boolean {
    return this.src[this.nextNonSpace[i + 1]!] === ")";
  }

  /** The index of the closing character of a title that opens at i and is
   *  followed by `\s*\)`, or -1. The regex tries the longest title first, so
   *  this is the first close no backslash precedes, or failing that, the last
   *  earlier close a backslash precedes. */
  private titleClose(i: number): number {
    const open = this.src[i];
    const close = open === "(" ? ")" : open === '"' || open === "'" ? open : null;
    if (close === null) return -1;
    const hard = this.nextHardClose.get(close)![i + 1]!;
    if (hard < this.n && this.closesLink(hard)) return hard;
    const soft = this.prevSoftClose.get(close)![hard - 1]!;
    return soft > i ? soft : -1;
  }

  /** The end of an angle-bracket destination opening at i, or -1. */
  private angleEnd(i: number): number {
    const src = this.src;
    let count = 0;
    for (let j = i + 1; j < this.n; j++) {
      const ch = src[j];
      if (ch === "\\") {
        if (j + 1 >= this.n || LINE_TERMINATORS.includes(src[j + 1]!)) return -1;
        j++;
      } else if (ch === ">") {
        return count > 0 ? j : -1;
      } else if (ch === "\n" || ch === "\r" || ch === "<") {
        return -1;
      }
      count++;
    }
    return -1;
  }

  /** The optional title and `\s*\)` after a destination ending at i. marked
   *  turns each `\r\n` and `\r` into `\n` before it reads anything, so a `\r`
   *  counts as the newline its grammar allows before a title. */
  private afterDestination(i: number): Omit<LinkTail, "destStart" | "destEnd"> | null {
    let s = this.nextNonBlank[i]!;
    if (this.src[s] === "\r" && this.src[s + 1] === "\n") s++;
    if (this.src[s] === "\n" || this.src[s] === "\r") s = this.nextNonBlank[s + 1]!;
    const titleClose = s < this.n ? this.titleClose(s) : -1;
    if (titleClose >= 0) return { titleStart: s, titleEnd: titleClose + 1, end: this.nextNonSpace[titleClose + 1]! + 1 };
    const j = this.nextNonSpace[i]!;
    return this.src[j] === ")" ? { titleStart: -1, titleEnd: -1, end: j + 1 } : null;
  }

  /** The tail of a link whose destination starts at i. */
  private destinationAt(i: number): LinkTail | null {
    if (this.src[i] === "<") {
      const angle = this.angleEnd(i);
      const after = angle >= 0 ? this.afterDestination(angle + 1) : null;
      if (after) return { destStart: i, destEnd: angle + 1, ...after };
    }
    const bareEnd = this.bareEnd[i]!;
    const after = this.afterDestination(bareEnd);
    if (after) return { destStart: i, destEnd: bareEnd, ...after };
    const q = bareEnd > i ? this.prevEndsBareDestination[bareEnd - 1]! : -1;
    if (q < i) return null;
    const ends = this.endsBareDestination[q]!;
    return ends >= 0
      ? { destStart: i, destEnd: q, titleStart: q, titleEnd: ends + 1, end: this.nextNonSpace[ends + 1]! + 1 }
      : { destStart: i, destEnd: q, titleStart: -1, titleEnd: -1, end: this.nextNonSpace[q]! + 1 };
  }

  /** The tail after the `]` at index i, when a `(` follows it. */
  private tail(i: number): LinkTail | null {
    if (this.src[i + 1] !== "(") return null;
    const open = i + 2;
    for (let j = this.nextNonSpace[open]!; j >= open; j--) {
      const tail = this.destinationAt(j);
      if (tail) {
        this.tails.set(i, tail);
        return tail;
      }
    }
    return null;
  }

  /**
   * marked's checks on the destination from start to end, done without
   * slicing it. Returns null when marked rejects it, -2 when a `(` is left
   * open (which also rejects the link), -1 when the whole destination stands,
   * or the index within it of the `)` where findClosingBracket cuts the link
   * short. A destination never starts inside a run of backslashes, so `depth`
   * counts the same escapes findClosingBracket does.
   */
  destinationCut(start: number, end: number): number | null {
    const first = this.nextNonSpace[start]!;
    if (first >= end) return -1;
    if (this.src[first] === "<") {
      const last = this.prevNonSpace[end - 1]!;
      if (this.src[last] !== ">") return null;
      return this.backslashRun[last - 1]! % 2 === 1 ? null : -1;
    }
    if (this.nextCloseParen[start]! >= end) return -1;
    const drop = this.nextDepthDrop[start]!;
    if (drop <= end) return drop - 1 - start;
    return this.depth[end]! > this.depth[start]! ? -2 : -1;
  }

  /** The link the regex matches with its text starting at `textStart`:
   *  where the text ends, the tail after it, and where marked cuts the
   *  destination (see destinationCut). */
  match(textStart: number): { textEnd: number; tail: LinkTail; cut: number } | null {
    const textEnd = this.textEnd[textStart]!;
    if (textEnd < 0) return null;
    const tail = this.tails.get(textEnd)!;
    const cut = this.destinationCut(tail.destStart, tail.destEnd);
    return cut === null || cut === -2 ? null : { textEnd, tail, cut };
  }

  /** The link or image that starts exactly at `index`, or null. */
  linkAt(index: number): MarkdownLink | null {
    const src = this.src;
    const image = src[index] === "!";
    const open = image ? index + 1 : index;
    if (src[open] !== "[") return null;
    const found = this.match(open + 1);
    if (!found) return null;
    const { textEnd, tail, cut } = found;
    const text = src.slice(open + 1, textEnd);
    const destination = src.slice(tail.destStart, cut >= 0 ? tail.destStart + cut : tail.destEnd).trim();
    const title = cut < 0 && tail.titleStart >= 0 ? src.slice(tail.titleStart, tail.titleEnd) : "";
    let end = tail.end;
    if (cut >= 0) {
      // marked cuts the link `cut + 1` characters after the `(`, counting any
      // space before the destination, so the link in `[a]( b)c)` is `[a]( b`
      // and the `)` after it is plain text. It reads each `\r\n` as one.
      const space = src.slice(textEnd + 2, tail.destStart);
      const reach = cut + 1 - (space.length - (space.split("\r\n").length - 1));
      end = index + src.slice(index, reach > 0 ? tail.destStart + reach : textEnd + 2).trimEnd().length;
    }
    const href = destination.startsWith("<") ? destination.slice(1, -1) : destination;
    return {
      start: index,
      end,
      image,
      text,
      textStart: open + 1,
      destination,
      href: href.replace(ESCAPED_PUNCTUATION, "$1"),
      title: title.slice(1, -1).replace(ESCAPED_PUNCTUATION, "$1"),
    };
  }

  /** The first link or image at or after `from`. An escaped `[` opens no
   *  link, and an escaped `!` makes the link that follows it a plain link. */
  next(from: number): MarkdownLink | null {
    const i = from < this.n ? this.nextOpen[from]! : this.n;
    if (i >= this.n) return null;
    return this.linkAt(i > from && this.src[i - 1] === "!" && !this.isEscaped(i - 1) ? i - 1 : i);
  }
}

/** A scanner for `src`, or null when it holds no `](` and so no link. */
function scan(src: string): LinkScanner | null {
  return src.includes("](") ? new LinkScanner(src) : null;
}

/**
 * Finds the link or image that starts exactly at a given index of `src`.
 * Building the matcher reads the whole string once, and each call after that
 * takes time only in proportion to the link it returns.
 */
export function markdownLinkMatcher(src: string): (index: number) => MarkdownLink | null {
  const scanner = scan(src);
  return (index) => scanner?.linkAt(index) ?? null;
}

/** The link or image that starts exactly at `index`, or null. This reads the
 *  whole of `src`; to match at many indexes of one string, use
 *  markdownLinkMatcher. */
export function matchMarkdownLink(src: string, index: number): MarkdownLink | null {
  return scan(src)?.linkAt(index) ?? null;
}

/** The first link or image at or after `from`. An escaped `[` opens no link,
 *  and an escaped `!` makes the link that follows it a plain link. */
export function nextMarkdownLink(src: string, from = 0): MarkdownLink | null {
  return scan(src)?.next(from) ?? null;
}

/** Every link and image in `src`, in order. Links inside another link's text
 *  are not included. */
export function findMarkdownLinks(src: string): MarkdownLink[] {
  const scanner = scan(src);
  const links: MarkdownLink[] = [];
  for (let link = scanner?.next(0) ?? null; link; link = scanner!.next(link.end)) links.push(link);
  return links;
}

/**
 * Replaces each link and image in `src` with what `replace` returns for it.
 * When `replace` returns null the link is kept, and `replace` is called for
 * each link inside its text, such as the image in `[![alt](a.png)](url)`.
 * For those links, `source` is the outer link's text and offsets index into it.
 */
export function replaceMarkdownLinks(
  src: string,
  replace: (link: MarkdownLink, source: string) => string | null,
): string {
  const scanner = scan(src);
  if (!scanner) return src;
  let out = "";
  let pos = 0;
  for (let link = scanner.next(0); link; link = scanner.next(pos)) {
    const replacement = replace(link, src);
    out += src.slice(pos, link.start);
    if (replacement !== null) {
      out += replacement;
    } else {
      // The text is read as a string of its own, as marked reads it. Texts
      // at one depth don't overlap, and only a code span in a link's text
      // can hold a link whose own text holds one, so this stays linear.
      const textEnd = link.textStart + link.text.length;
      out += src.slice(link.start, link.textStart) + replaceMarkdownLinks(link.text, replace) + src.slice(textEnd, link.end);
    }
    pos = link.end;
  }
  return out + src.slice(pos);
}

/**
 * `src.replace(pattern, replace)` for the text outside links and images.
 * Each link is replaced with what `replaceLink` returns for it, or kept as
 * written when that is null or there is no `replaceLink`. Whichever of a link
 * and a `pattern` match starts first wins, as if both were alternatives in one
 * regex, and the link wins a tie. So a match that starts before a link and
 * runs into it, such as a code span containing a link, goes to `replace` whole.
 */
export function replaceOutsideMarkdownLinks(
  src: string,
  pattern: RegExp,
  replace: (match: RegExpExecArray) => string,
  replaceLink?: (link: MarkdownLink) => string | null,
): string {
  const re = new RegExp(pattern.source, pattern.flags.replace(/[gy]/g, "") + "g");
  const scanner = scan(src);
  let out = "";
  let pos = 0;
  let link = scanner?.next(0) ?? null;
  // `m` is the first match at or after `searchedFrom`, or null if there is
  // none. It stays correct as `pos` advances up to `m.index`, so the regex
  // reruns only once `pos` passes it.
  let m: RegExpExecArray | null = null;
  let searchedFrom = -1;
  for (;;) {
    if (link && link.start < pos) link = scanner!.next(pos);
    if (searchedFrom < 0 || (m && m.index < pos)) {
      re.lastIndex = pos;
      m = re.exec(src);
      searchedFrom = pos;
    }
    if (link && (!m || link.start <= m.index)) {
      out += src.slice(pos, link.start) + (replaceLink?.(link) ?? src.slice(link.start, link.end));
      pos = link.end;
      continue;
    }
    if (!m) break;
    out += src.slice(pos, m.index) + replace(m);
    pos = m.index + m[0].length;
    if (m[0].length === 0) {
      out += src.slice(pos, pos + 1);
      pos++;
    }
    searchedFrom = -1;
  }
  return out + src.slice(pos);
}

/**
 * Writes `href` as a destination this grammar reads back as the same href.
 * Whitespace and control characters are percent-encoded, as marked does to an
 * href when it renders it, so such an href comes back encoded but names the
 * same URL. Backslashes, parentheses, `"` and `<` are backslash-escaped.
 */
export function toMarkdownDestination(href: string): string {
  return href
    .replace(/[\s\x00-\x1f\x7f]/g, (ch) => encodeURIComponent(ch))
    .replace(/[\\()"<]/g, "\\$&");
}

/** Writes a link or image, as `[text](destination "title")`, that this grammar
 *  reads back with the same text, href and title. */
export function formatMarkdownLink(link: { image?: boolean; text: string; href: string; title?: string }): string {
  const title = link.title ? ` "${link.title.replace(/[\\"]/g, "\\$&")}"` : "";
  return `${link.image ? "!" : ""}[${link.text}](${toMarkdownDestination(link.href)}${title})`;
}
